import { create } from "zustand";
import type {
  OnboardingState,
  GoalCode,
  LifeSituation,
  CoachTone,
  AvailableMinutes,
  PreferredTime,
  PathwayStyle,
} from "@andrum/schemas";
import { goalsNeedPathway } from "@andrum/schemas";

interface OnboardingStore extends OnboardingState {
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  toggleGoal: (goal: GoalCode) => void;
  toggleLifeSituation: (value: LifeSituation) => void;
  /** @deprecated */
  setLifeSituation: (value: LifeSituation) => void;
  setJoyPathway: (value: PathwayStyle) => void;
  setCalmPathway: (value: PathwayStyle) => void;
  setBaseline: (baseline: OnboardingState["baseline"]) => void;
  setAvailableMinutes: (value: AvailableMinutes) => void;
  setCoachTone: (value: CoachTone) => void;
  setPreferredTime: (value: PreferredTime) => void;
  setNotificationsEnabled: (value: boolean) => void;
  complete: () => void;
  reset: () => void;
  needsPathwayStep: () => boolean;
}

/**
 * Steps:
 * 0 welcome · 1 method · 2 goals · 3 pathway (skip if no joy/calm)
 * 4 life multi · 5 baseline · 6 time · 7 tone · 8 when · 9 notifs
 */
export const LAST_STEP = 9;

const initial: OnboardingState = {
  step: 0,
  goals: [],
  lifeSituations: [],
  completed: false,
};

function needsPathway(goals: GoalCode[]): boolean {
  const n = goalsNeedPathway(goals);
  return n.joy || n.calm;
}

function advanceFrom(step: number, goals: GoalCode[], dir: 1 | -1): number {
  let next = step + dir;
  if (dir === 1 && step === 2 && !needsPathway(goals)) {
    next = 4; // skip pathway
  }
  if (dir === -1 && step === 4 && !needsPathway(goals)) {
    next = 2; // skip pathway backwards
  }
  return Math.min(LAST_STEP, Math.max(0, next));
}

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  ...initial,
  setStep: (step) => set({ step }),
  nextStep: () =>
    set({ step: advanceFrom(get().step, get().goals, 1) }),
  prevStep: () =>
    set({ step: advanceFrom(get().step, get().goals, -1) }),
  toggleGoal: (goal) => {
    const current = get().goals;
    if (current.includes(goal)) {
      const goals = current.filter((g) => g !== goal);
      set({
        goals,
        joyPathway: goals.includes("joy") ? get().joyPathway : undefined,
        calmPathway: goals.includes("calm") ? get().calmPathway : undefined,
      });
    } else if (current.length < 3) {
      set({ goals: [...current, goal] });
    }
  },
  toggleLifeSituation: (value) => {
    const current = get().lifeSituations ?? [];
    if (value === "prefer_not_to_say") {
      set({
        lifeSituations: current.includes("prefer_not_to_say")
          ? []
          : ["prefer_not_to_say"],
        lifeSituation: "prefer_not_to_say",
      });
      return;
    }
    // Remove prefer_not_to_say if selecting real situations
    let next = current.filter((s) => s !== "prefer_not_to_say");
    if (next.includes(value)) {
      next = next.filter((s) => s !== value);
    } else if (next.length < 3) {
      next = [...next, value];
    }
    set({
      lifeSituations: next,
      lifeSituation: next[0],
    });
  },
  setLifeSituation: (lifeSituation) =>
    set({
      lifeSituation,
      lifeSituations: [lifeSituation],
    }),
  setJoyPathway: (joyPathway) => set({ joyPathway }),
  setCalmPathway: (calmPathway) => set({ calmPathway }),
  setBaseline: (baseline) => set({ baseline }),
  setAvailableMinutes: (availableMinutes) => set({ availableMinutes }),
  setCoachTone: (coachTone) => set({ coachTone }),
  setPreferredTime: (preferredTime) => set({ preferredTime }),
  setNotificationsEnabled: (notificationsEnabled) =>
    set({ notificationsEnabled }),
  complete: () => set({ completed: true, step: LAST_STEP }),
  reset: () => set(initial),
  needsPathwayStep: () => needsPathway(get().goals),
}));
